import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatTime } from '../models/rooms'

function getComposeAppointmentItem(): Office.AppointmentCompose | null {
    const item = Office.context?.mailbox?.item

    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        return null
    }

    return item as Office.AppointmentCompose
}

function getTimeValue(time: Office.Time): Promise<Date> {
    return new Promise((resolve, reject) => {
        time.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value)
                return
            }

            reject(result.error)
        })
    })
}

function addItemHandler(eventType: Office.EventType, handler: () => void): Promise<void> {
    return new Promise((resolve, reject) => {
        const item = Office.context?.mailbox?.item

        if (!item) {
            resolve()
            return
        }

        item.addHandlerAsync(eventType, handler, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve()
                return
            }

            reject(result.error)
        })
    })
}

export function useAppointmentSchedule(enabled: boolean) {
    const appointmentTimeHandlerBound = useRef(false)
    const [appointmentStart, setAppointmentStart] = useState<Date | null>(null)
    const [appointmentEnd, setAppointmentEnd] = useState<Date | null>(null)
    const [currentEventDate, setCurrentEventDate] = useState('')

    const startTimeLabel = useMemo(() => formatTime(appointmentStart), [appointmentStart])
    const endTimeLabel = useMemo(() => formatTime(appointmentEnd), [appointmentEnd])

    const syncAppointmentTimeToFilters = useCallback(async () => {
        const appointment = getComposeAppointmentItem()

        if (!appointment) {
            setAppointmentStart(null)
            setAppointmentEnd(null)
            setCurrentEventDate('')
            return
        }

        try {
            const [startUtc, endUtc] = await Promise.all([getTimeValue(appointment.start), getTimeValue(appointment.end)])

            setAppointmentStart(startUtc)
            setAppointmentEnd(endUtc)
            setCurrentEventDate(startUtc.toISOString().slice(0, 10))
        } catch (error) {
            console.error('Failed to sync appointment time.', error)
        }
    }, [])

    useEffect(() => {
        if (!enabled) {
            return
        }

        void syncAppointmentTimeToFilters()

        if (appointmentTimeHandlerBound.current || !Office.context.requirements.isSetSupported('Mailbox', '1.7')) {
            return
        }

        void addItemHandler(Office.EventType.AppointmentTimeChanged, () => {
            void syncAppointmentTimeToFilters()
        })
            .then(() => {
                appointmentTimeHandlerBound.current = true
            })
            .catch((error) => {
                console.error('Failed to subscribe to appointment time changes.', error)
            })
    }, [enabled, syncAppointmentTimeToFilters])

    return {
        appointmentStart,
        appointmentEnd,
        currentEventDate,
        startTimeLabel,
        endTimeLabel,
    }
}
